import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Circle, Mail, Loader2, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useVerification } from '../contexts/VerificationContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * VerificationPage
 *
 * Blocks the entire authenticated app until the user enters the 6-digit
 * code emailed to them. Shows email status (Verified / Not Verified), a
 * masked email address, the code input, a Resend Code button (rate-
 * limited), and a Verify and Continue button.
 */
export default function VerificationPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const {
    required,
    verified,
    emailMasked,
    codeTtlMinutes,
    refresh,
  } = useVerification();

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  // If the user isn't gated (admin / grandfathered / already verified),
  // send them to their normal dashboard.
  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (!required || verified) {
      const dest = user.role === 'admin' ? '/admin' : user.role === 'supervisor' ? '/supervisor' : '/employee';
      navigate(dest, { replace: true });
    }
  }, [user, required, verified, navigate]);

  const handleResend = async () => {
    setResending(true);
    try {
      const { data } = await axios.post(`${API}/verification/send-email-code`);
      toast.success(data.message || 'A new code has been sent.');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to resend code. Please try again.';
      toast.error(msg);
    } finally {
      setResending(false);
    }
  };

  const handleVerify = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (!code || !/^\d{6}$/.test(code)) {
      toast.error('Please enter the 6-digit code from your email.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/verification/verify-email`, { code });
      toast.success(data.message || 'Email verified successfully.');
      // Refresh context state and navigate to the onboarding flow.
      await refresh();
      const dest = user.role === 'supervisor' ? '/supervisor/my-profile' : '/employee/my-profile';
      navigate(dest, { replace: true });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to verify code.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md" data-testid="verification-page">
        <CardHeader className="space-y-2 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-yellow-500/10 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-yellow-500" />
          </div>
          <CardTitle className="text-2xl">Verify your account</CardTitle>
          <CardDescription>
            Confirm your email address to continue setting up your San Security account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Email verification status row */}
          <div className="flex items-center justify-between p-3 rounded-md border">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-muted-foreground" />
              <div>
                <div className="text-sm font-medium" data-testid="verification-email-masked">
                  {emailMasked || '—'}
                </div>
                <div className="text-xs text-muted-foreground">
                  Email address
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5" data-testid="verification-email-status">
              {verified ? (
                <>
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span className="text-xs text-green-600 font-medium">Verified</span>
                </>
              ) : (
                <>
                  <Circle className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Not Verified</span>
                </>
              )}
            </div>
          </div>

          {/* Code input */}
          <form onSubmit={handleVerify} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">Verification code</Label>
              <Input
                id="code"
                data-testid="verification-code-input"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="text-center text-2xl tracking-[0.5em] font-mono"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                We sent a 6-digit code to your email. It expires in {codeTtlMinutes} minutes.
              </p>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={submitting || code.length !== 6}
              data-testid="verification-verify-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying…</>
              ) : (
                'Verify and Continue'
              )}
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleResend}
              disabled={resending}
              data-testid="verification-resend-btn"
            >
              {resending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
              ) : (
                'Resend Email Code'
              )}
            </Button>
          </form>

          <div className="pt-2 text-center">
            <button
              type="button"
              onClick={() => { logout(); navigate('/login', { replace: true }); }}
              className="text-xs text-muted-foreground hover:text-foreground underline"
              data-testid="verification-signout-btn"
            >
              Sign out
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
